import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from 'mdi-react/CloseIcon';
import Select, { components } from 'react-select';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useRecoilValue } from 'recoil';
import { useGetFacilityServices } from '../../App/customize/hooks/useGetFacilityServices';
import { useGetAppointmentTypes } from '../../App/customize/hooks/useGetAppointmentTypes';
import { useAppointmentLabels } from '../../App/customize/hooks/useAppointmentLabels';
import PetSearchSelect from '../../common/PetSearchSelect';
import AppointmentTypesSelect from '../../common/AppointmentTypesSelect';
import { DEFAULT_TIMESTAMP_FORMAT } from '../../../constants/constants';
import { formatPhone } from '../../../libs/commonHelper';
import localStorage from '../../../libs/storageHelper';
import { PTB_RELOAD_EVENT } from '../../Web/PetTrackingBoard/constants';
import { handleRequest } from '../../../redux/services/APIService';
import { useFocusedUtilityEscape } from '../../../hooks/useFocusedUtilityEscape';
import { useAddToTrackingBoardUtility } from './AddToTrackingBoardUtility.hooks';
import { addToTrackingBoardUtilityStateAtom } from './AddToTrackingBoardUtility.state';
import {
  formatStaffDoctorOptionLabel,
  mapStaffDoctorsFromApi,
  mergeStaffDoctorSelectStyles,
} from '../../../utils/staffDoctorSelect';

const SIDEBAR_GAP = 16;
const VIEWPORT_MARGIN = 12;
/** Stable viewport top near Logout band; launcher position is ignored. */
const PREFERRED_TOP = 260;
const SIDEBAR_TRANSITION_FALLBACK_MS = 300;
const DRAG_THRESHOLD_PX = 5;
const SUCCESS_NOTICE_MS = 3750;
const DEFAULT_PET_AVATAR = `${process.env.PUBLIC_URL}/img/default-animal-black.png`;
const DAYCARE_TYPE_TITLE = 'Daycare';
const BOARDING_TYPE_TITLE = 'Boarding';
const VET_SEARCH_INPUT_NAME = 'addToBoardSearch';
const DAYCARE_SEARCH_INPUT_NAME = 'addToBoardSearchDaycare';
const BOARDING_SEARCH_INPUT_NAME = 'addToBoardSearchBoarding';
const DUPLICATE_SAME_DAY_CODE = 'DUPLICATE_APPOINTMENT_SAME_DAY';

const DOCTOR_SELECT_STYLES = {
  control: (base, state) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: 13,
    borderColor: state.isFocused ? '#00b0ec' : '#e0e2e0',
    boxShadow: 'none',
    borderRadius: 4,
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 8px',
    height: 30,
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: 30,
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 8px',
    color: '#50514f',
    display: 'flex',
    alignItems: 'center',
  }),
  clearIndicator: (base) => ({
    ...base,
    padding: '0 4px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#15153e',
    fontSize: 13,
    fontWeight: 400,
    fontFamily: 'inherit',
    margin: 0,
  }),
  singleValue: (base) => ({
    ...base,
    fontSize: 13,
    color: '#15153e',
  }),
  menu: (base) => ({
    ...base,
    backgroundColor: '#e6e6e6',
  }),
  menuList: (base) => ({
    ...base,
    backgroundColor: '#e6e6e6',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000,
  }),
};

function AtbDoctorDropdownIndicator(props) {
  return (
    <components.DropdownIndicator {...props}>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        aria-hidden="true"
        focusable="false"
      >
        <path
          d="M3 4.5 L6 7.5 L9 4.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </components.DropdownIndicator>
  );
}

function getLayoutMetrics() {
  const sidebar = document.querySelector('.sidebar');
  const sidebarRect = sidebar?.getBoundingClientRect();
  const sidebarRight = sidebarRect?.right ?? 240;
  const addPetLauncher = document.querySelector('[data-add-pet-launcher]');
  const launcherTitle = addPetLauncher?.querySelector('.sidebar__link-title');
  const anchorRect = (launcherTitle || addPetLauncher)?.getBoundingClientRect();
  const preferredTop =
    anchorRect != null ? Math.round(anchorRect.top) : PREFERRED_TOP;
  return {
    sidebarRight,
    preferredTop,
    preferredLeft: Math.round(sidebarRight + SIDEBAR_GAP),
  };
}

function resolvePanelPosition(panelRect) {
  const { sidebarRight, preferredTop, preferredLeft } = getLayoutMetrics();
  const width = panelRect?.width || 0;
  const height = panelRect?.height || 0;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    viewportWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    viewportHeight - height - VIEWPORT_MARGIN,
  );

  let left = Math.min(Math.max(preferredLeft, VIEWPORT_MARGIN), maxLeft);
  const top = Math.min(Math.max(preferredTop, VIEWPORT_MARGIN), maxTop);

  const sidebarSafeLeft = sidebarRight + SIDEBAR_GAP;
  const fitsBesideSidebar =
    width > 0 &&
    sidebarSafeLeft + width + VIEWPORT_MARGIN <= viewportWidth;

  if (fitsBesideSidebar) {
    left = Math.max(left, sidebarSafeLeft);
    left = Math.min(left, maxLeft);
  }

  return {
    top: Math.round(top),
    left: Math.round(left),
  };
}

function clampToViewport(top, left, panelRect) {
  const width = panelRect?.width || 0;
  const height = panelRect?.height || 0;
  const maxLeft = Math.max(
    VIEWPORT_MARGIN,
    window.innerWidth - width - VIEWPORT_MARGIN,
  );
  const maxTop = Math.max(
    VIEWPORT_MARGIN,
    window.innerHeight - height - VIEWPORT_MARGIN,
  );
  return {
    top: Math.round(Math.min(Math.max(top, VIEWPORT_MARGIN), maxTop)),
    left: Math.round(Math.min(Math.max(left, VIEWPORT_MARGIN), maxLeft)),
  };
}

function parentDisplayName(parent) {
  if (!parent) {
    return '';
  }
  if (parent.name) {
    return parent.name;
  }
  return `${parent.firstName || ''} ${parent.lastName || ''}`.trim();
}

function parentPhoneDisplay(parent) {
  const digits = String(parent?.phone || '').replace(/\D/g, '');
  if (!digits) {
    return '';
  }
  return formatPhone(digits, '(###) ###-####');
}

function getRoundedMomentTime() {
  const date = moment();

  if (date.minute() <= 10 && date.minute() > 0) {
    date.minute(0);
  } else {
    const roundedUp = Math.ceil(date.minute() / 15) * 15;
    date.minute(roundedUp);
  }
  date.second(0);
  return date;
}

function isOnTrackingBoardRoute() {
  return window.location.pathname === '/tracking-board';
}

function isOnAppointmentsRoute() {
  return window.location.pathname === '/appointments';
}

function isDuplicateSameDayError(error) {
  const payload = error?.response?.data;
  const text = [
    payload?.errorMessage,
    payload?.error,
    payload?.data,
    payload?.message,
    error?.message,
  ]
    .filter(Boolean)
    .join(' ');
  return text.includes(DUPLICATE_SAME_DAY_CODE);
}

function getCreateErrorMessage(error, fallbackMessage) {
  return (
    error?.response?.data?.errorMessage ||
    error?.response?.data?.message ||
    error?.response?.data?.data?.resultMessage ||
    error?.data?.message ||
    error?.message ||
    fallbackMessage
  );
}

function focusSearchField(inputName = VET_SEARCH_INPUT_NAME) {
  requestAnimationFrame(() => {
    const el = document.querySelector(
      `.add-to-tracking-board-utility input[name="${inputName}"]`,
    );
    if (el && typeof el.focus === 'function') {
      el.focus();
    }
  });
}

function boardingSearchInputName(searchKind) {
  return searchKind === 'boarding'
    ? BOARDING_SEARCH_INPUT_NAME
    : DAYCARE_SEARCH_INPUT_NAME;
}

function boardingSearchTypeTitle(searchKind) {
  return searchKind === 'boarding' ? BOARDING_TYPE_TITLE : DAYCARE_TYPE_TITLE;
}

async function isPetAlreadyOnPtb(facilityId, utcOffset, petId) {
  if (!facilityId || !petId) {
    return false;
  }

  try {
    const results = await handleRequest('get', 'ptb/appointment', true, null, {
      facilityId,
      offset: utcOffset ?? 0,
    });
    const events = results?.data;
    if (!Array.isArray(events)) {
      return false;
    }
    return events.some(
      (event) => String(event.petId) === String(petId),
    );
  } catch {
    return false;
  }
}

export default function AddToTrackingBoardUtility() {
  const { open } = useRecoilValue(addToTrackingBoardUtilityStateAtom);
  const { closeAddToTrackingBoardUtility } = useAddToTrackingBoardUtility();
  const { data: services } = useGetFacilityServices();
  const { data: appointmentTypes } = useGetAppointmentTypes({
    filter: { checked: 1 },
  });
  const labels = useAppointmentLabels();
  const isBoardingOnly = !!services?.isBoardingOnly;
  // Fast path is boarding-only. Set this false to restore the generic
  // selected-pet / Type / Add workflow for a boarding facility.
  const useBoardingFastPath = isBoardingOnly;
  const facilityFromProfile = useSelector(
    (state) => state.user?.profile?.facility,
  );
  const loggedUser = localStorage.getFromStorage('loggedUser');
  const facilityId =
    facilityFromProfile?._id || loggedUser?.user?.facility || null;
  const facilityUtcOffset = facilityFromProfile?.utcOffset || 0;

  const [selectedPet, setSelectedPet] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [appointmentType, setAppointmentType] = useState('');
  const [activeBoardingSearch, setActiveBoardingSearch] = useState('daycare');
  const [staffDoctorList, setStaffDoctorList] = useState([]);
  const [doctorMenuOpen, setDoctorMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusNotice, setStatusNotice] = useState('');
  const [position, setPosition] = useState({
    top: PREFERRED_TOP,
    left: 256,
  });
  const panelRef = useRef(null);
  const sidebarTransitionCleanupRef = useRef(null);
  const userMovedRef = useRef(false);
  const positionRef = useRef({ top: PREFERRED_TOP, left: 256 });
  const dragRef = useRef(null);
  const statusNoticeTimerRef = useRef(null);
  const selectPetCheckRef = useRef(0);
  const submitLockRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  positionRef.current = position;

  const clearStatusNoticeTimer = useCallback(() => {
    if (statusNoticeTimerRef.current) {
      clearTimeout(statusNoticeTimerRef.current);
      statusNoticeTimerRef.current = null;
    }
  }, []);

  const clearSidebarTransitionWatch = useCallback(() => {
    if (sidebarTransitionCleanupRef.current) {
      sidebarTransitionCleanupRef.current();
      sidebarTransitionCleanupRef.current = null;
    }
  }, []);

  const resetForm = useCallback(() => {
    setSelectedPet(null);
    setDoctor(null);
    setAppointmentType('');
    setDoctorMenuOpen(false);
    setSubmitting(false);
  }, []);

  const applyPosition = useCallback(() => {
    const panelEl = panelRef.current;
    const rect = panelEl ? panelEl.getBoundingClientRect() : null;

    if (userMovedRef.current) {
      const current = positionRef.current;
      const next = rect
        ? clampToViewport(current.top, current.left, rect)
        : current;
      setPosition((prev) =>
        prev.top === next.top && prev.left === next.left ? prev : next,
      );
      return;
    }

    const next = rect
      ? resolvePanelPosition(rect)
      : (() => {
          const { preferredTop, preferredLeft } = getLayoutMetrics();
          return { top: preferredTop, left: preferredLeft };
        })();
    setPosition((prev) =>
      prev.top === next.top && prev.left === next.left ? prev : next,
    );
  }, []);

  const updatePosition = useCallback(() => {
    applyPosition();
    requestAnimationFrame(() => {
      requestAnimationFrame(applyPosition);
    });
  }, [applyPosition]);

  const watchSidebarWidthTransition = useCallback(() => {
    clearSidebarTransitionWatch();
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) {
      updatePosition();
      return;
    }

    const onTransitionEnd = (event) => {
      if (event.target !== sidebar || event.propertyName !== 'width') {
        return;
      }
      clearSidebarTransitionWatch();
      updatePosition();
    };

    const fallbackTimer = setTimeout(() => {
      clearSidebarTransitionWatch();
      updatePosition();
    }, SIDEBAR_TRANSITION_FALLBACK_MS);

    sidebar.addEventListener('transitionend', onTransitionEnd);
    sidebarTransitionCleanupRef.current = () => {
      clearTimeout(fallbackTimer);
      sidebar.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [clearSidebarTransitionWatch, updatePosition]);

  const showDuplicateSameDayNotice = useCallback(
    (petName, inputName = VET_SEARCH_INPUT_NAME) => {
      clearStatusNoticeTimer();
      resetForm();
      setStatusNotice(`${petName} ${labels.alreadyOnTrackingBoardToday}`);
      requestAnimationFrame(() => {
        updatePosition();
        focusSearchField(inputName);
      });
      statusNoticeTimerRef.current = setTimeout(() => {
        statusNoticeTimerRef.current = null;
        setStatusNotice('');
        requestAnimationFrame(updatePosition);
      }, SUCCESS_NOTICE_MS);
    },
    [
      clearStatusNoticeTimer,
      labels.alreadyOnTrackingBoardToday,
      resetForm,
      updatePosition,
    ],
  );

  const closeUtility = useCallback(() => {
    selectPetCheckRef.current += 1;
    submitLockRef.current = false;
    userMovedRef.current = false;
    dragRef.current = null;
    setIsDragging(false);
    clearStatusNoticeTimer();
    setStatusNotice('');
    setActiveBoardingSearch('daycare');
    resetForm();
    closeAddToTrackingBoardUtility();
  }, [
    clearStatusNoticeTimer,
    closeAddToTrackingBoardUtility,
    resetForm,
  ]);

  const createAppointmentForPet = useCallback(
    async ({
      pet,
      parent,
      typeId,
      focusInputName = VET_SEARCH_INPUT_NAME,
    }) => {
      if (!pet || !parent || !typeId || submitting || submitLockRef.current) {
        return;
      }

      submitLockRef.current = true;
      clearStatusNoticeTimer();
      setStatusNotice('');
      setSubmitting(true);

      const selectedType = (appointmentTypes || []).find(
        (type) => String(type._id) === String(typeId),
      );
      const isDaycare = selectedType?.title === DAYCARE_TYPE_TITLE;
      const payload = {
        parentId: parent._id,
        pet: pet._id,
        time: getRoundedMomentTime().format('hh:mm A'),
        type: typeId,
        typeOther: '',
        doctor: doctor || null,
        referringDoctor: null,
        shareUpdates: false,
        appointmentDate: moment().format(DEFAULT_TIMESTAMP_FORMAT),
        createdFromTrackingBoard: true,
        suppressNotifications: true,
      };
      if (isBoardingOnly && !isDaycare) {
        payload.placeInBoarding = true;
      }

      try {
        const response = await handleRequest(
          'post',
          'appointment',
          true,
          payload,
        );
        const appointmentId =
          response?.data?._id ?? response?.data?.id ?? null;
        if (isOnTrackingBoardRoute()) {
          if (appointmentId != null) {
            window.dispatchEvent(
              new CustomEvent(PTB_RELOAD_EVENT, {
                detail: { appointmentId: String(appointmentId) },
              }),
            );
          } else {
            window.dispatchEvent(new Event(PTB_RELOAD_EVENT));
          }
        }
        if (isOnAppointmentsRoute()) {
          window.dispatchEvent(new Event('loyal-appointments-refresh'));
        }
        const petName = pet.name;
        resetForm();
        setStatusNotice(`${petName} ${labels.addedToTrackingBoard}`);
        requestAnimationFrame(() => {
          updatePosition();
          focusSearchField(focusInputName);
        });
        statusNoticeTimerRef.current = setTimeout(() => {
          statusNoticeTimerRef.current = null;
          setStatusNotice('');
          requestAnimationFrame(updatePosition);
        }, SUCCESS_NOTICE_MS);
      } catch (error) {
        setSubmitting(false);
        if (isDuplicateSameDayError(error)) {
          showDuplicateSameDayNotice(pet.name, focusInputName);
          return;
        }
        setStatusNotice(
          getCreateErrorMessage(error, labels.couldNotAddToTrackingBoard),
        );
        requestAnimationFrame(() => {
          updatePosition();
          focusSearchField(focusInputName);
        });
      } finally {
        submitLockRef.current = false;
      }
    },
    [
      appointmentTypes,
      clearStatusNoticeTimer,
      doctor,
      isBoardingOnly,
      labels.addedToTrackingBoard,
      labels.couldNotAddToTrackingBoard,
      resetForm,
      showDuplicateSameDayNotice,
      submitting,
      updatePosition,
    ],
  );

  const handleSelectPet = useCallback(
    async ({ pet, parent }) => {
      if (!pet?._id) {
        return;
      }

      const checkId = ++selectPetCheckRef.current;

      if (facilityId) {
        const alreadyOnBoard = await isPetAlreadyOnPtb(
          facilityId,
          facilityUtcOffset,
          pet._id,
        );
        if (checkId !== selectPetCheckRef.current) {
          return;
        }
        if (alreadyOnBoard) {
          showDuplicateSameDayNotice(pet.name);
          return;
        }
      }

      setSelectedPet({ pet, parent });
      setDoctor(null);
      setAppointmentType('');
      setStatusNotice('');
      requestAnimationFrame(() => {
        updatePosition();
        requestAnimationFrame(() => {
          const panel = panelRef.current;
          if (!panel) {
            return;
          }
          const doctorInput = panel.querySelector(
            '.atb-doctor-select__input',
          );
          const typeSelect = panel.querySelector(
            'select[name="appointmentType"]',
          );
          const target = doctorInput || typeSelect || panel;
          if (target && typeof target.focus === 'function') {
            target.focus({ preventScroll: true });
          }
        });
      });
    },
    [
      facilityId,
      facilityUtcOffset,
      showDuplicateSameDayNotice,
      updatePosition,
    ],
  );

  const handleBoardingSelectPet = useCallback(
    async ({ pet, parent }, searchKind) => {
      if (!pet?._id || submitting || submitLockRef.current) {
        return;
      }

      const inputName = boardingSearchInputName(searchKind);
      const checkId = ++selectPetCheckRef.current;
      setActiveBoardingSearch(searchKind);

      if (facilityId) {
        const alreadyOnBoard = await isPetAlreadyOnPtb(
          facilityId,
          facilityUtcOffset,
          pet._id,
        );
        if (checkId !== selectPetCheckRef.current) {
          return;
        }
        if (alreadyOnBoard) {
          showDuplicateSameDayNotice(pet.name, inputName);
          return;
        }
      }

      const matchedType = (appointmentTypes || []).find(
        (type) => type.title === boardingSearchTypeTitle(searchKind),
      );
      if (!matchedType?._id) {
        setStatusNotice(labels.couldNotAddToTrackingBoard);
        requestAnimationFrame(() => {
          updatePosition();
          focusSearchField(inputName);
        });
        return;
      }

      await createAppointmentForPet({
        pet,
        parent,
        typeId: matchedType._id,
        focusInputName: inputName,
      });
    },
    [
      appointmentTypes,
      createAppointmentForPet,
      facilityId,
      facilityUtcOffset,
      labels.couldNotAddToTrackingBoard,
      showDuplicateSameDayNotice,
      submitting,
      updatePosition,
    ],
  );

  const handleClearSelectedPet = useCallback(() => {
    setSelectedPet(null);
    setDoctor(null);
    setDoctorMenuOpen(false);
    setAppointmentType('');
    setStatusNotice('');
    requestAnimationFrame(() => {
      updatePosition();
      focusSearchField();
    });
  }, [updatePosition]);

  const handleCancel = useCallback(() => {
    if (selectedPet) {
      handleClearSelectedPet();
      return;
    }
    closeUtility();
  }, [closeUtility, handleClearSelectedPet, selectedPet]);

  const handleAdd = useCallback(() => {
    const pet = selectedPet?.pet;
    const parent = selectedPet?.parent;
    if (!pet || !parent || !appointmentType || submitting) {
      return undefined;
    }
    return createAppointmentForPet({
      pet,
      parent,
      typeId: appointmentType,
      focusInputName: VET_SEARCH_INPUT_NAME,
    });
  }, [
    appointmentType,
    createAppointmentForPet,
    selectedPet,
    submitting,
  ]);

  const onTitlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) {
      return;
    }
    e.preventDefault();
    setDoctorMenuOpen(false);
    const target = e.currentTarget;
    if (typeof target.setPointerCapture === 'function') {
      target.setPointerCapture(e.pointerId);
    }
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      originTop: positionRef.current.top,
      originLeft: positionRef.current.left,
      moved: false,
    };
  }, []);

  const onTitlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) {
      return;
    }
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.moved = true;
      userMovedRef.current = true;
      setIsDragging(true);
    }
    const panelEl = panelRef.current;
    const rect = panelEl
      ? panelEl.getBoundingClientRect()
      : { width: 0, height: 0 };
    const next = clampToViewport(
      drag.originTop + dy,
      drag.originLeft + dx,
      rect,
    );
    setPosition(next);
  }, []);

  const endTitlePointerDrag = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) {
      return;
    }
    const target = e.currentTarget;
    if (
      typeof target.hasPointerCapture === 'function' &&
      target.hasPointerCapture(e.pointerId) &&
      typeof target.releasePointerCapture === 'function'
    ) {
      target.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    selectPetCheckRef.current += 1;
    submitLockRef.current = false;
    userMovedRef.current = false;
    dragRef.current = null;
    setIsDragging(false);
    clearStatusNoticeTimer();
    setStatusNotice('');
    setActiveBoardingSearch('daycare');
    resetForm();
    updatePosition();
    focusSearchField(
      useBoardingFastPath ? DAYCARE_SEARCH_INPUT_NAME : VET_SEARCH_INPUT_NAME,
    );

    const onResize = () => updatePosition();
    window.addEventListener('resize', onResize);

    const sidebar = document.querySelector('.sidebar');
    const observer = sidebar
      ? new MutationObserver(() => {
          updatePosition();
          watchSidebarWidthTransition();
        })
      : null;
    if (sidebar && observer) {
      observer.observe(sidebar, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    return () => {
      window.removeEventListener('resize', onResize);
      if (observer) {
        observer.disconnect();
      }
      clearSidebarTransitionWatch();
    };
  }, [
    clearSidebarTransitionWatch,
    clearStatusNoticeTimer,
    open,
    resetForm,
    updatePosition,
    useBoardingFastPath,
    watchSidebarWidthTransition,
  ]);

  useEffect(() => {
    if (!open || isBoardingOnly) {
      return undefined;
    }
    let cancelled = false;
    handleRequest('get', 'staff-doctors/by-facility', true)
      .then((results) => {
        if (!cancelled) {
          setStaffDoctorList(mapStaffDoctorsFromApi(results?.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStaffDoctorList([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, isBoardingOnly]);

  useFocusedUtilityEscape({
    enabled: open,
    panelRef,
    onEscape: handleCancel,
  });

  useEffect(
    () => () => {
      clearSidebarTransitionWatch();
      clearStatusNoticeTimer();
    },
    [clearSidebarTransitionWatch, clearStatusNoticeTimer],
  );

  if (!open) {
    return null;
  }

  const pet = selectedPet?.pet;
  const parent = selectedPet?.parent;
  const parentPhone = parentPhoneDisplay(parent);
  const canAdd = Boolean(pet) && Boolean(appointmentType) && !submitting;
  const typeLabel = 'Type';

  return createPortal(
    <div
      ref={panelRef}
      className="add-to-tracking-board-utility"
      style={{ top: position.top, left: position.left }}
      tabIndex={-1}
    >
      <div className="add-to-tracking-board-utility__panel">
        <div
          className={
            'add-to-tracking-board-utility__title' +
            (isDragging
              ? ' add-to-tracking-board-utility__title--dragging'
              : '')
          }
          onPointerDown={onTitlePointerDown}
          onPointerMove={onTitlePointerMove}
          onPointerUp={endTitlePointerDrag}
          onPointerCancel={endTitlePointerDrag}
        >
          {labels.addToTrackingBoard}
        </div>
        <form
          className="add-to-tracking-board-utility__body"
          autoComplete="off"
          noValidate
          onSubmit={(e) => e.preventDefault()}
        >
          {useBoardingFastPath ? (
            <>
              <div className="add-to-tracking-board-utility__fields add-to-tracking-board-utility__fields--boarding">
                <label className="add-to-tracking-board-utility__field">
                  <span
                    className={
                      'add-to-tracking-board-utility__label' +
                      (activeBoardingSearch === 'daycare'
                        ? ' add-to-tracking-board-utility__label--active'
                        : '')
                    }
                  >
                    Daycare
                  </span>
                  <PetSearchSelect
                    autoFocus
                    inputName={DAYCARE_SEARCH_INPUT_NAME}
                    placeholder="Search"
                    disabled={submitting}
                    onFocus={() => setActiveBoardingSearch('daycare')}
                    onSelectPet={(selection) =>
                      handleBoardingSelectPet(selection, 'daycare')
                    }
                  />
                </label>
                <label className="add-to-tracking-board-utility__field">
                  <span
                    className={
                      'add-to-tracking-board-utility__label' +
                      (activeBoardingSearch === 'boarding'
                        ? ' add-to-tracking-board-utility__label--active'
                        : '')
                    }
                  >
                    Boarding
                  </span>
                  <PetSearchSelect
                    inputName={BOARDING_SEARCH_INPUT_NAME}
                    placeholder="Search"
                    disabled={submitting}
                    onFocus={() => setActiveBoardingSearch('boarding')}
                    onSelectPet={(selection) =>
                      handleBoardingSelectPet(selection, 'boarding')
                    }
                  />
                </label>
              </div>
              <div className="add-to-tracking-board-utility__actions">
                <button
                  type="button"
                  className="add-to-tracking-board-utility__cancel"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
              {statusNotice ? (
                <p className="add-to-tracking-board-utility__status-hint">
                  {statusNotice}
                </p>
              ) : null}
            </>
          ) : (
            <>
              {pet ? (
                <div className="add-to-tracking-board-utility__selected">
                  <img
                    className="add-to-tracking-board-utility__selected-avatar"
                    src={pet.image || DEFAULT_PET_AVATAR}
                    alt=""
                  />
                  <span className="add-to-tracking-board-utility__selected-names">
                    <span className="add-to-tracking-board-utility__selected-line">
                      <span className="add-to-tracking-board-utility__selected-pet-name">
                        {pet.name}
                      </span>
                      <span className="add-to-tracking-board-utility__selected-separator">
                        {' \u2014 '}
                      </span>
                      <span className="add-to-tracking-board-utility__selected-parent-name">
                        {parentDisplayName(parent)}
                      </span>
                    </span>
                    {parentPhone ? (
                      <span className="add-to-tracking-board-utility__selected-phone">
                        {parentPhone}
                      </span>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="add-to-tracking-board-utility__selected-clear"
                    aria-label="Clear selected pet"
                    onClick={handleClearSelectedPet}
                  >
                    <CloseIcon size={16} />
                  </button>
                </div>
              ) : (
                <PetSearchSelect
                  autoFocus
                  placeholder="Search"
                  onSelectPet={handleSelectPet}
                />
              )}
              {pet ? (
                <div
                  className={
                    'add-to-tracking-board-utility__fields' +
                    (isBoardingOnly
                      ? ' add-to-tracking-board-utility__fields--boarding'
                      : '')
                  }
                >
                  {!isBoardingOnly ? (
                    <label className="add-to-tracking-board-utility__field">
                      <span className="add-to-tracking-board-utility__label">
                        Doctor
                      </span>
                      <Select
                        classNamePrefix="atb-doctor-select"
                        value={
                          staffDoctorList.find(
                            (option) => option.value === doctor,
                          ) || null
                        }
                        onChange={(option) => {
                          setDoctor(option ? option.value : null);
                          requestAnimationFrame(updatePosition);
                        }}
                        options={staffDoctorList}
                        formatOptionLabel={formatStaffDoctorOptionLabel}
                        placeholder="Select Doctor"
                        isClearable
                        name="doctor"
                        menuIsOpen={doctorMenuOpen}
                        onMenuOpen={() => setDoctorMenuOpen(true)}
                        onMenuClose={() => setDoctorMenuOpen(false)}
                        onKeyDown={(e) => {
                          if (e.key !== 'Escape' && e.keyCode !== 27) {
                            return;
                          }
                          e.preventDefault();
                          e.stopPropagation();
                          setDoctorMenuOpen(false);
                          handleCancel();
                        }}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        menuShouldScrollIntoView={false}
                        components={{
                          DropdownIndicator: AtbDoctorDropdownIndicator,
                        }}
                        styles={mergeStaffDoctorSelectStyles(DOCTOR_SELECT_STYLES)}
                      />
                    </label>
                  ) : null}
                  <label className="add-to-tracking-board-utility__field">
                    <span className="add-to-tracking-board-utility__label">
                      {typeLabel}{' '}
                      <span className="add-to-tracking-board-utility__required">
                        *
                      </span>
                    </span>
                    <AppointmentTypesSelect
                      value={appointmentType}
                      handleChange={(e) => {
                        setAppointmentType(e.target.value);
                        setStatusNotice('');
                        requestAnimationFrame(updatePosition);
                      }}
                    />
                  </label>
                </div>
              ) : null}
              {isBoardingOnly ? (
                <>
                  <div className="add-to-tracking-board-utility__actions">
                    <button
                      type="button"
                      className="add-to-tracking-board-utility__cancel"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    {pet ? (
                      <button
                        type="button"
                        className="add-to-tracking-board-utility__add"
                        disabled={!canAdd}
                        onClick={handleAdd}
                      >
                        Add
                      </button>
                    ) : null}
                  </div>
                  {statusNotice ? (
                    <p className="add-to-tracking-board-utility__status-hint">
                      {statusNotice}
                    </p>
                  ) : null}
                </>
              ) : (
                <div className="add-to-tracking-board-utility__footer add-to-tracking-board-utility__footer--vet">
                  {statusNotice ? (
                    <p className="add-to-tracking-board-utility__status-hint">
                      {statusNotice}
                    </p>
                  ) : null}
                  <div className="add-to-tracking-board-utility__actions">
                    <button
                      type="button"
                      className="add-to-tracking-board-utility__cancel"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    {pet ? (
                      <button
                        type="button"
                        className="add-to-tracking-board-utility__add"
                        disabled={!canAdd}
                        onClick={handleAdd}
                      >
                        Add
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </>
          )}
        </form>
      </div>
    </div>,
    document.body,
  );
}
